import { FormEvent, useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './GracefulFailureDemoAgentic.module.css';

type RowState = 'waiting' | 'active' | 'done' | 'error';
type Phase = 'idle' | 'running' | 'paused' | 'adding' | 'resuming' | 'complete';
type Resolution = 'email' | 'sms' | 'skip';

interface Candidate {
  name: string;
  contact: string;
}

const CANDIDATES: Candidate[] = [
  { name: 'Ana Ruiz', contact: 'email' },
  { name: 'Mei Tanaka', contact: 'email' },
  { name: 'Oskar Lind', contact: 'email' },
  { name: 'Priya Nair', contact: 'email' },
  { name: 'Jonah Price', contact: 'none' },
];

const STEP_MS = 650;

export function GracefulFailureDemoAgentic() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [rows, setRows] = useState<RowState[]>(CANDIDATES.map(() => 'waiting'));
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('Start the workflow. One of the five candidates is missing something.');
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const setRow = (i: number, s: RowState) => setRows((r) => r.map((x, j) => (j === i ? s : x)));

  const start = () => {
    setPhase('running');
    setFeedback('Sending invites...');
    CANDIDATES.forEach((c, i) => {
      later(() => setRow(i, 'active'), STEP_MS * i * 2);
      later(() => {
        if (c.contact === 'none') {
          setRow(i, 'error');
          setPhase('paused');
          setFeedback('The agent paused on the failed step instead of skipping it. The other four invites are sent. Pick a way forward.');
        } else {
          setRow(i, 'done');
        }
      }, STEP_MS * (i * 2 + 1));
    });
  };

  const resolve = (r: Resolution) => {
    setResolution(r);
    setPhase('resuming');
    setRow(4, r === 'skip' ? 'error' : 'active');
    setFeedback('Resuming from the exact step that paused...');
    later(() => {
      if (r !== 'skip') setRow(4, 'done');
      setPhase('complete');
      setFeedback(
        r === 'skip'
          ? 'Jonah was skipped on purpose and the log says so - no silent "5 of 5". Ref INV-2291 stays attached.'
          : `Jonah's invite went out by ${r === 'sms' ? 'SMS' : 'email'}. Nothing was redone; the workflow finished from where it stopped.`
      );
    }, 900);
  };

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setFeedback('That doesn\'t look like an email address yet. Try something like jonah.price@mailbox.com.');
      return;
    }
    resolve('email');
  };

  const rowText = (c: Candidate, i: number) => {
    const s = rows[i];
    if (s === 'error') {
      return resolution === 'skip'
        ? `Skipped ${c.name} - no email on application (your choice)`
        : `Paused - couldn't invite ${c.name}: no email address on his application`;
    }
    if (s === 'done' && i === 4 && resolution === 'sms') return `Invite sent to ${c.name} by SMS`;
    if (s === 'done' && i === 4 && resolution === 'email') return `Invite sent to ${c.name} at ${email}`;
    if (s === 'done') return `Invite sent to ${c.name}`;
    if (s === 'active') return `Sending invite to ${c.name}...`;
    return `Invite ${c.name}`;
  };

  const sentCount = rows.filter((r) => r === 'done').length;
  const dotClass: Record<RowState, string> = {
    waiting: '',
    active: kit.logDotActive,
    done: kit.logDotDone,
    error: resolution === 'skip' ? '' : kit.logDotError,
  };

  return (
    <div className={kit.root}>
      <div className={kit.row}>
        <span className={kit.label}>Workflow · Interview invites · Product Designer</span>
        <span className={kit.chip}>
          {sentCount} of {CANDIDATES.length} sent
        </span>
      </div>

      <div className={kit.log} aria-label="Agent activity">
        {CANDIDATES.map((c, i) => (
          <div className={`${kit.logRow} ${rows[i] === 'waiting' ? styles.waiting : ''}`} key={c.name}>
            <span className={kit.logTime}>{i === 4 && rows[i] === 'done' ? '11:43' : '11:40'}</span>
            <span className={`${kit.logDot} ${dotClass[rows[i]]}`} aria-hidden="true" />
            <span>{rowText(c, i)}</span>
            {rows[i] === 'error' && resolution !== 'skip' ? (
              <span className={`${kit.chip} ${kit.chipBad}`}>Paused</span>
            ) : rows[i] === 'error' ? (
              <span className={kit.chip}>Skipped</span>
            ) : (
              <span />
            )}
          </div>
        ))}
      </div>

      {phase === 'idle' && (
        <div className={kit.row}>
          <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={start}>
            Send invites to 5 candidates
          </button>
        </div>
      )}

      {(phase === 'paused' || phase === 'adding') && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Paused · step 5 of 5</span>
            <span>Ref INV-2291</span>
          </div>
          <h3 className={kit.proposalTitle}>Jonah Price's invite isn't sent</h3>
          <dl className={kit.fields}>
            <dt>Trying to</dt>
            <dd>Email an interview invite for Product Designer</dd>
            <dt>Missing</dt>
            <dd>An email address - his application only has a phone number</dd>
            <dt>Kept</dt>
            <dd>4 invites already sent, nothing to redo</dd>
          </dl>

          {phase === 'adding' ? (
            <form className={styles.form} onSubmit={submitEmail}>
              <label className={kit.label} htmlFor="gf-email">
                Jonah's email
              </label>
              <div className={kit.row}>
                <input
                  id="gf-email"
                  className={styles.input}
                  type="email"
                  placeholder="jonah.price@mailbox.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <button type="submit" className={`${kit.btn} ${kit.btnApprove}`}>
                  Send invite
                </button>
                <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={() => setPhase('paused')}>
                  Back
                </button>
              </div>
            </form>
          ) : (
            <div className={`${kit.row} ${styles.actions}`}>
              <button
                type="button"
                className={`${kit.btn} ${kit.btnPrimary}`}
                onClick={() => {
                  setPhase('adding');
                  setFeedback('The agent asks for exactly what it needs - one email address.');
                }}
              >
                Add email
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => resolve('sms')}>
                Send by SMS instead
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={() => resolve('skip')}>
                Skip Jonah
              </button>
            </div>
          )}
        </div>
      )}

      {phase === 'complete' && (
        <div className={kit.row}>
          <span className={`${kit.chip} ${resolution === 'skip' ? kit.chipWarn : kit.chipGood}`}>
            {resolution === 'skip' ? 'Complete · 4 of 5, 1 skipped' : 'Complete · 5 of 5'}
          </span>
          <span className={kit.label}>Ref INV-2291</span>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
